import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";

// Third party
import * as Yup from "yup";

// Material
import { Box, TextField } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Autocomplete } from "@material-ui/lab";

// Styling
import { commonStyle } from "../../../commonStyle";
import { useStyles } from "../style";

// Utils
import { PageTypes } from "../../../utils/SlickCareData";
import useValidator from "../../../utils/useValidator";

// Components
import PrimaryButton from "../../../Components/UI/Buttons/PrimaryButton";
import SecondaryButton from "../../../Components/UI/Buttons/SecondaryButton";
import CustomCard from "../../../Components/UI/CustomCard";
import FormLayout from "../../../Components/UI/FormLayout";
import SkeletonLoader from "../../../Components/UI/SkeletonLoader";

// Actions
import { fetchCareHomeGroups } from "../../../Stores/CareHomeGroups/actions";
import { searchSlickCareProfiles } from "../../../Stores/SlickCareProfiles/actions";
import {
  addWebsite,
  editWebsite,
  getViewData,
} from "../../../Stores/Website/actions";

const WebsiteAdd = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const userInfo = useSelector((state) => state.AuthReducer.user);
  const isEditing = useRouteMatch("/d/website/edit/:id")?.isExact;
  const classes = useStyles();
  const commonClasses = commonStyle();
  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  const { isFetching, currentWebsite, isSubmitting } = useSelector(
    (state) => state.WebsiteReducer
  );
  const { isFetchingLocations, SlickCareProfiles } = useSelector(
    (state) => state.SlickCareProfilesReducer
  );
  const { isFetchingCareHomeGroups, careHomeGroups } = useSelector(
    (state) => state.CareHomeGroupsReducer
  );
  let loading = isFetching || isSubmitting;

  const [open, setOpen] = React.useState(false);
  const [openCompany, setOpenCompany] = React.useState(false);
  // function onSubmit() {
  //   if (isEditing) {
  //     dispatch(editWebsite({ history, id }));
  //   } else {
  //     dispatch(
  //       addWebsite({
  //         history,
  //       })
  //     );
  //   }
  //   clearFormState();
  // }

  function onSubmit() {
    if (isEditing) {
      dispatch(editWebsite({ values, history }));
    } else {
      dispatch(addWebsite({ values, history }));
    }
  }

  const {
    errors,
    touched,
    values,
    handleSubmit,
    handleChange,
    handleBlur,
    setValues,
    clearFormState,
    getFieldProps,
  } = useValidator({
    initialValues: {
      // Personal
      title: "",
      type: "",
      company_id: "",
      location_id: "",
    },
    onSubmit,
    validationSchema: Yup.object({
      title: Yup.string().required("Title is Required!"),
      type: Yup.string().required("Type is Required!"),
      company_id: Yup.string().required("Company is Required!"),
      location_id: Yup.string()
        .nullable()
        .when("type", {
          is: "Location",
          then: Yup.string().required("Location is required!"),
        }),
    }),
  });

  useEffect(() => {
    if (id && isEditing) {
      dispatch(getViewData(id));
    }
    if (!isEditing) {
      clearFormState();
    }
  }, [id, isEditing]);

  useEffect(() => {
    if (currentWebsite?.id) {
      setValues({
        ...values,
        id: currentWebsite.id,
        type: currentWebsite.type,
        title: currentWebsite.title,
        company_details: currentWebsite?.company ?? {},
      });
    }
  }, [currentWebsite]);

  useEffect(() => {
    dispatch(
      searchSlickCareProfiles({
        page: 1,
        limit: 10,
        admin_id: userInfo?.id,
      })
    );
  }, []);

  useEffect(() => {
    dispatch(
      fetchCareHomeGroups({
        page: 1,
        limit: 10,
        admin_id: userInfo?.id,
      })
    );
  }, []);

  return (
    <FormLayout
      title={isEditing ? `Edit Website` : `Add Website`}
      mode={isEditing ? `Edit` : `Add`}
      pageName="Website"
      parentLink="/d/website"
      childLink="/d/website/add"
      customTitle={currentWebsite?.id ? currentWebsite?.title : ""}
    >
      <>
        <form onSubmit={handleSubmit}>
          <Box className={commonClasses.flexBox}>
            <Box className={commonClasses.flexItem}>
              <CustomCard title="Website Details">
                <Box className={commonClasses.flexBox}>
                  <Box className={commonClasses.flexItemMD}>
                    {loading ? (
                      <SkeletonLoader type="text" height={40} />
                    ) : (
                      <TextField
                        variant="outlined"
                        size="small"
                        label="Title"
                        placeholder="Title"
                        value={values.title}
                        helperText={touched.title && errors.title}
                        error={!!(touched.title && errors.title)}
                        {...getFieldProps("title")}
                        className={commonClasses.formInput}
                      />
                    )}
                  </Box>
                  <Box className={commonClasses.flexItemMD}>
                    {loading ? (
                      <SkeletonLoader type="text" height={40} />
                    ) : (
                      <Autocomplete
                        id="combo-box-demo"
                        options={PageTypes}
                        getOptionLabel={(option) => (option ? option : "")}
                        value={values.type}
                        onChange={(event, newValue) => {
                          setValues({
                            ...values,
                            type: newValue ?? "",
                            location_id:
                              newValue === "General" ? "" : values?.location_id,
                            location_detail:
                              newValue === "General"
                                ? {}
                                : values?.location_detail,
                          });
                        }}
                        classes={{
                          root: classes.rootSecondary,
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            placeholder={`Select Type`}
                            label={`Type`}
                            helperText={touched.type && errors.type}
                            error={!!(touched.type && errors.type)}
                          />
                        )}
                      />
                    )}
                  </Box>
                  <Box className={commonClasses.flexItemMD}>
                    {loading ? (
                      <SkeletonLoader type="text" height={40} />
                    ) : (
                      <Autocomplete
                        options={
                          careHomeGroups?.data?.length > 0
                            ? careHomeGroups?.data?.map((ele) => ({
                                id: ele.id,
                                address: ele.address,
                                name: ele.name,
                              }))
                            : []
                        }
                        loading={isFetchingCareHomeGroups}
                        open={openCompany}
                        onOpen={() => {
                          setOpenCompany(true);
                        }}
                        onClose={() => {
                          setOpenCompany(false);
                        }}
                        renderOption={(option) => (
                          <Box>
                            {option.id &&
                              `${option.name !== null ? option.name : ""}`}
                          </Box>
                        )}
                        getOptionLabel={(option) =>
                          option?.id ? option.name : ""
                        }
                        getOptionSelected={(option, value) =>
                          value?.id === option?.id
                        }
                        style={{ width: "100%" }}
                        value={
                          values?.company_details
                            ? values?.company_details
                            : currentWebsite?.company
                        }
                        onChange={(event, newValue) => {
                          setValues({
                            ...values,
                            company_id: newValue?.id ?? "",
                            company_details: newValue ?? {},
                          });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            onChange={(e) => {
                              setLocationSearchTerm(e.target.value);
                            }}
                            helperText={touched.company_id && errors.company_id}
                            error={!!(touched.company_id && errors.company_id)}
                            placeholder="Company"
                            label="Company"
                            variant="outlined"
                            size="small"
                            id="standard-basic"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {isFetchingCareHomeGroups ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </React.Fragment>
                              ),
                            }}
                          />
                        )}
                      />
                    )}
                  </Box>
                  <Box className={commonClasses.flexItemMD}>
                    {loading ? (
                      <SkeletonLoader type="text" height={40} />
                    ) : (
                      <Autocomplete
                        options={SlickCareProfiles?.data ?? []}
                        loading={isFetchingLocations}
                        open={open}
                        onOpen={() => {
                          setOpen(true);
                        }}
                        onClose={() => {
                          setOpen(false);
                        }}
                        renderOption={(option) => (
                          <Box>
                            <div>
                              {option.id &&
                                `${option.title !== null ? option.title : ""}`}
                            </div>
                          </Box>
                        )}
                        getOptionLabel={(option) =>
                          option?.id ? option.title : ""
                        }
                        getOptionSelected={(option, value) =>
                          value?.id === option?.id
                        }
                        style={{ width: "100%" }}
                        value={
                          values.location_details
                            ? values.location_details
                            : currentWebsite?.location_details
                        }
                        onChange={(event, newValue) => {
                          setValues({
                            ...values,
                            location_id: newValue?.id ?? "",
                            location_details: newValue ?? {},
                          });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            onChange={(e) => {
                              setLocationSearchTerm(e.target.value);
                            }}
                            helperText={
                              touched.location_id && errors.location_id
                            }
                            error={
                              !!(touched.location_id && errors.location_id)
                            }
                            placeholder="Location"
                            label="Location"
                            variant="outlined"
                            size="small"
                            id="standard-basic"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {isFetchingLocations ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </React.Fragment>
                              ),
                            }}
                          />
                        )}
                      />
                    )}
                  </Box>
                </Box>
              </CustomCard>
            </Box>
          </Box>

          <div className={commonClasses.pageFormFooter}>
            <div className={commonClasses.flexBetweeen}>
              <SecondaryButton onClick={() => history.goBack()}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" hideIcon={true}>
                {isEditing ? "Save" : "Add"}
              </PrimaryButton>
            </div>
          </div>
        </form>
      </>
    </FormLayout>
  );
};

export default WebsiteAdd;
